import { from, of } from "rxjs";
import { createEpicMiddleware, combineEpics, ofType } from "redux-observable";

import { catchError, map, switchMap, concatMap, mergeMap, debounceTime } from "rxjs/operators";

import analytics from "shared/analytics";

import { getApolloClient } from "../../graphql/apolloClient";
import {
  EcAnalysesDocument,
  EcAssetGroupsDocument,
  EcComingSoonDocument,
  EcMediaResourcesDocument,
  EcRecentUpdatesDocument,
  EcSetUserAnalysisDocument,
  EcUserAnalysesDocument,
  EcViewerDocument,
} from "../../graphql/generated/graphql";

// Apollo adds __typename at every level; nothing reads it, and leaving it in nested
// objects (e.g. viewer.organization) would leak it into component props.
const stripTypename = (value) => {
  if (Array.isArray(value)) return value.map(stripTypename);
  if (value === null || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => key !== "__typename")
      .map(([key, nested]) => [key, stripTypename(nested)]),
  );
};

const toRecord = (record) => (record ? stripTypename(record) : null);
const toCollection = (records) => (records || []).map(stripTypename);

const query = (document) => getApolloClient().query({ query: document });
const mutate = (document, variables) => getApolloClient().mutate({ mutation: document, variables });

export const actionTypes = {
  INITIALIZE_AFTER_LOGIN: "INITIALIZE_AFTER_LOGIN",
  FETCH_ANALYSES: "FETCH_ANALYSES",
  FETCH_ANALYSES_SUCCESS: "FETCH_ANALYSES_SUCCESS",
  FETCH_ANALYSES_FAILURE: "FETCH_ANALYSES_FAILURE",
  FETCH_USER_PROFILE: "FETCH_USER_PROFILE",
  FETCH_USER_PROFILE_SUCCESS: "FETCH_USER_PROFILE_SUCCESS",
  FETCH_USER_PROFILE_FAILURE: "FETCH_USER_PROFILE_FAILURE",
  FETCH_MEDIA_RESOURCES: "FETCH_MEDIA_RESOURCES",
  FETCH_MEDIA_RESOURCES_SUCCESS: "FETCH_MEDIA_RESOURCES_SUCCESS",
  FETCH_MEDIA_RESOURCES_FAILURE: "FETCH_MEDIA_RESOURCES_FAILURE",
  FETCH_RECENT_UPDATES: "FETCH_RECENT_UPDATES",
  FETCH_RECENT_UPDATES_SUCCESS: "FETCH_RECENT_UPDATES_SUCCESS",
  FETCH_RECENT_UPDATES_FAILURE: "FETCH_RECENT_UPDATES_FAILURE",
  FETCH_ASSET_GROUPS: "FETCH_ASSET_GROUPS",
  FETCH_ASSET_GROUPS_SUCCESS: "FETCH_ASSET_GROUPS_SUCCESS",
  FETCH_ASSET_GROUPS_FAILURE: "FETCH_ASSET_GROUPS_FAILURE",
  SET_GRID_FILTER: "SET_GRID_FILTER",
  SET_SORT: "SET_SORT",
  APPLY_SORT: "APPLY_SORT",
  GET_USER_INPUTS: "GET_USER_INPUTS",
  GET_USER_INPUTS_SUCCESS: "GET_USER_INPUTS_SUCCESS",
  GET_USER_INPUTS_FAILURE: "GET_USER_INPUTS_FAILURE",
  SET_USER_INPUT: "SET_USER_INPUT",
  SET_USER_INPUT_SUCCESS: "SET_USER_INPUT_SUCCESS",
  SET_USER_INPUT_FAILURE: "SET_USER_INPUT_FAILURE",
  FETCH_COMING_SOON: "FETCH_COMING_SOON",
  FETCH_COMING_SOON_SUCCESS: "FETCH_COMING_SOON_SUCCESS",
  FETCH_COMING_SOON_FAILURE: "FETCH_COMING_SOON_FAILURE",
};

const initializeAfterLoginEpic = (action$) =>
  action$.pipe(
    ofType(actionTypes.INITIALIZE_AFTER_LOGIN),
    // Dispatch the following three actions:
    mergeMap(() =>
      of(
        {
          type: actionTypes.GET_USER_INPUTS,
        },
        {
          type: actionTypes.FETCH_USER_PROFILE,
        },
        {
          type: actionTypes.FETCH_MEDIA_RESOURCES,
        },
        {
          type: actionTypes.FETCH_RECENT_UPDATES,
        },
        {
          type: actionTypes.FETCH_ANALYSES,
        },
        {
          type: actionTypes.FETCH_ASSET_GROUPS,
        },
        {
          type: actionTypes.FETCH_COMING_SOON,
        },
      ),
    ),
  );

const analysesEpic = (action$, state$, { api }) =>
  action$.pipe(
    ofType(actionTypes.FETCH_ANALYSES),
    // debounceTime(200), // rxjs/operators, e.g.: for auto-suggest
    // delay(5000), // rxjs/operators to delay 5 seconds, e.g.: to implement/test a loader
    // switchMap: pending subscriptions are cancelled (use only last emitted value)
    switchMap(() =>
      from(api.fetchAnalyses()).pipe(
        map((result) => ({
          type: actionTypes.FETCH_ANALYSES_SUCCESS,
          payload: result,
        })),
        // Uncaught errors that reach action$.pipe() will terminate it, and
        // it will no longer listen for new actions:
        catchError((error) =>
          of({
            type: actionTypes.FETCH_ANALYSES_FAILURE,
            // error could be structured further, but redux state needs to consist of serializable POJOs:
            payload: String(error),
            error: true,
          }),
        ),
      ),
    ),
  );

const userProfileEpic = (action$, state$, { api }) =>
  action$.pipe(
    ofType(actionTypes.FETCH_USER_PROFILE),
    switchMap(() =>
      from(api.fetchUserProfile()).pipe(
        map((result) => ({
          type: actionTypes.FETCH_USER_PROFILE_SUCCESS,
          payload: result,
        })),
        catchError((error) =>
          of({
            type: actionTypes.FETCH_USER_PROFILE_FAILURE,
            payload: String(error),
            error: true,
          }),
        ),
      ),
    ),
  );

const mediaResourcesEpic = (action$, state$, { api }) =>
  action$.pipe(
    ofType(actionTypes.FETCH_MEDIA_RESOURCES),
    switchMap(() =>
      from(api.fetchMediaResources()).pipe(
        map((result) => ({
          type: actionTypes.FETCH_MEDIA_RESOURCES_SUCCESS,
          payload: result,
        })),
        catchError((error) =>
          of({
            type: actionTypes.FETCH_MEDIA_RESOURCES_FAILURE,
            payload: String(error),
            error: true,
          }),
        ),
      ),
    ),
  );

const recentUpdatesEpic = (action$, state$, { api }) =>
  action$.pipe(
    ofType(actionTypes.FETCH_RECENT_UPDATES),
    switchMap(() =>
      from(api.fetchRecentUpdates()).pipe(
        map((result) => ({
          type: actionTypes.FETCH_RECENT_UPDATES_SUCCESS,
          payload: result,
        })),
        catchError((error) =>
          of({
            type: actionTypes.FETCH_RECENT_UPDATES_FAILURE,
            payload: String(error),
            error: true,
          }),
        ),
      ),
    ),
  );

const assetGroupsEpic = (action$, state$, { api }) =>
  action$.pipe(
    ofType(actionTypes.FETCH_ASSET_GROUPS),
    switchMap(() =>
      from(api.fetchAssetGroups()).pipe(
        map((result) => ({
          type: actionTypes.FETCH_ASSET_GROUPS_SUCCESS,
          payload: result,
        })),
        catchError((error) =>
          of({
            type: actionTypes.FETCH_ASSET_GROUPS_FAILURE,
            payload: String(error),
            error: true,
          }),
        ),
      ),
    ),
  );

const comingSoonEpic = (action$, state$, { api }) =>
  action$.pipe(
    ofType(actionTypes.FETCH_COMING_SOON),
    switchMap(() =>
      from(api.fetchComingSoon()).pipe(
        map((result) => ({
          type: actionTypes.FETCH_COMING_SOON_SUCCESS,
          payload: result,
        })),
        catchError((error) =>
          of({
            type: actionTypes.FETCH_COMING_SOON_FAILURE,
            payload: String(error),
            error: true,
          }),
        ),
      ),
    ),
  );

const applySortEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      actionTypes.FETCH_ANALYSES,
      actionTypes.FETCH_ANALYSES_SUCCESS,
      actionTypes.FETCH_ANALYSES_FAILURE,
      actionTypes.SET_SORT,
    ),
    // brief, async delay after updating column header state, before (more expensive) sorting:
    debounceTime(5),
    map(() => {
      const { analysesResponse, sort } = state$.value;
      return {
        type: actionTypes.APPLY_SORT,
        payload: { analysesResponse, sort },
      };
    }),
  );

const getUserInputsEpic = (action$, state$, { api }) =>
  action$.pipe(
    ofType(actionTypes.GET_USER_INPUTS),
    switchMap(() =>
      from(api.fetchUserInputs()).pipe(
        map((result) => {
          const payload = result.reduce(
            (acc, record) => ({ ...acc, [record.analysis_id]: record }),
            {},
          );
          return {
            type: actionTypes.GET_USER_INPUTS_SUCCESS,
            payload,
          };
        }),
        catchError((error) =>
          of({
            type: actionTypes.GET_USER_INPUTS_FAILURE,
            payload: String(error),
            error: true,
          }),
        ),
      ),
    ),
  );

const setUserInputEpic = (action$, state$, { api }) =>
  action$.pipe(
    ofType(actionTypes.SET_USER_INPUT),
    // concatMap: do not subscribe to the next Observable until the current one completes.

    concatMap(({ payload: { slug, key, value } }) =>
      from(api.updateUserInput(slug, key, value)).pipe(
        map((result) => {
          const { analysis_id: id, add_to_compare, user_proposed_price } = result;
          const payload = {
            [id]: {
              add_to_compare,
              user_proposed_price,
            },
          };
          return {
            type: actionTypes.SET_USER_INPUT_SUCCESS,
            payload,
          };
        }),
        catchError((error) =>
          of({
            type: actionTypes.SET_USER_INPUT_FAILURE,
            payload: String(error),
            error: true,
          }),
        ),
      ),
    ),
  );

class ECApi {
  constructor() {
    this.userCanSavePrice = false;
  }

  // Was an Observable subscription; the GraphQL client resolves a promise, so the
  // flag is set from the resolved payload instead.
  recordUserCanSavePrice = (viewer) => {
    const { users_can_save_price: userCanSavePrice = false } = viewer?.organization || {};
    this.userCanSavePrice = userCanSavePrice;
    analytics.userCanSavePrice = userCanSavePrice;
  };

  // REST also returned the specific_condition in a JSON:API `included` array, which
  // nothing read; there is deliberately no GraphQL equivalent.
  fetchAnalyses = () => query(EcAnalysesDocument).then(({ data }) => toCollection(data.analyses));

  // PATCH /api/users/me returns this same flat shape, so useChangeUser's
  // hand-dispatch into the slice stays consistent with what is fetched here.
  fetchUserProfile = () =>
    query(EcViewerDocument).then(({ data }) => {
      const viewer = toRecord(data.viewer);
      this.recordUserCanSavePrice(viewer);
      return viewer;
    });

  fetchMediaResources = () =>
    query(EcMediaResourcesDocument).then(({ data }) => toCollection(data.media_resources));

  // Migrated to GraphQL (#1083). Apollo rejects on GraphQL errors as well as
  // network errors under the default errorPolicy, so a partial response cannot be
  // mistaken for success -- the epic's catchError still sees a failure.
  fetchRecentUpdates = () =>
    query(EcRecentUpdatesDocument).then(({ data }) => toCollection(data.recent_updates));

  fetchAssetGroups = () =>
    query(EcAssetGroupsDocument).then(({ data }) => {
      // The nested media resource is requested by the document but unused by any
      // component, so it is dropped rather than pushed onto every report snapshot.
      return toCollection(data.asset_groups).map((record) => {
        const assetGroup = { ...record };
        delete assetGroup.media_resource;
        return assetGroup;
      });
    });

  // The asset group is nested on each record; comingSoonSelector reads it there.
  fetchComingSoon = () =>
    query(EcComingSoonDocument).then(({ data }) => toCollection(data.coming_soon_media_resources));

  fetchUserInputs = () =>
    query(EcUserAnalysesDocument).then(({ data }) => toCollection(data.user_analyses));

  updateUserInput = (analysisId, fieldKey, value) => {
    if (fieldKey === "user_proposed_price" && !this.userCanSavePrice) {
      // rx pipeables can be either an Observable, Promise, Array, or Iterable:
      return Promise.resolve();
    }
    return mutate(EcSetUserAnalysisDocument, {
      analysis_id: analysisId,
      [fieldKey]: value,
    }).then(({ data }) => {
      const { user_analysis: userAnalysis, errors } = data.set_user_analysis;
      // Expected failures ride in the payload with HTTP 200, where REST returned
      // 422. Reject so the epic's catchError still dispatches
      // SET_USER_INPUT_FAILURE rather than treating a refusal as success.
      if (errors && errors.length > 0) {
        throw new Error(errors.map((error) => error.message).join(", "));
      }
      return userAnalysis;
    });
  };
}

export const rootEpic = combineEpics(
  initializeAfterLoginEpic,
  analysesEpic,
  userProfileEpic,
  mediaResourcesEpic,
  recentUpdatesEpic,
  assetGroupsEpic,
  comingSoonEpic,
  applySortEpic,
  getUserInputsEpic,
  setUserInputEpic,
);

export default createEpicMiddleware({
  dependencies: { api: new ECApi() },
});
